<template>
  <b-form
    @submit.stop.prevent="handleSubmit"
    class="text-left"
  >
    <b-row>
      <b-col>
        <b-alert
          :show="hasBaseServerErrors()"
          variant="danger"
          dismissible
        >
          <p
            class="mb-0"
            v-for="(error, index) in baseServerErrors"
            :key="index"
          >
            {{ error }}
          </p>
        </b-alert>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          label="Template Name"
          label-for="quote-template-name"
        >
          <b-form-input
            id="quote-template-name"
            v-model="$v.form.name.$model"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            type="text"
          />
          <b-form-invalid-feedback
            v-if="$v.form.name.$dirty"
            id="quote-template-name-feedback"
          >
            <span v-if="!$v.form.name.required">Please enter a name.</span>
            <span v-if="!$v.form.name.maxLength">
              The name must be less than {{ $v.form.name.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.name.serverFailed">{{ serverErrors.name }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="6">
        <b-form-group
          label="Client"
          label-for="quote-template-client"
        >
          <client-select
            id="quote-template-client"
            :select-class="{ 'is-invalid': $v.form.client_id.$dirty && $v.form.client_id.$error, 'mb-1': true }"
            :value="form.client"
            @input="onClientChange"
            placeholder="Optional"
          />
          <b-form-invalid-feedback
            v-if="$v.form.client_id.$dirty"
            id="quote-template-client-feedback"
          >
            <span v-if="!$v.form.client_id.serverFailed">{{ serverErrors.client_id }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col lg="6">
        <b-form-group
          label="Service"
          label-for="quote-template-service"
        >
          <service-select
            id="quote-template-service"
            :select-class="{ 'is-invalid': $v.form.service_id.$dirty && $v.form.service_id.$error, 'mb-1': true }"
            :value="form.service"
            @input="onTemplateServiceChange"
            :client-id="form.client_id"
            client-unassigned
            placeholder="Optional"
          />
          <b-form-invalid-feedback
            v-if="$v.form.service_id.$dirty"
            id="quote-template-service-feedback"
          >
            <span v-if="!$v.form.service_id.serverFailed">{{ serverErrors.service_id }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          label="Description"
          label-for="quote-template-description"
        >
          <b-form-textarea
            id="quote-template-description"
            v-model="$v.form.description.$model"
            :state="$v.form.description.$dirty ? !$v.form.description.$error : null"
            rows="3"
            placeholder="Optional"
          />
          <b-form-invalid-feedback
            v-if="$v.form.description.$dirty"
            id="quote-template-description-feedback"
          >
            <span v-if="!$v.form.description.serverFailed">{{ serverErrors.description }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <h3 class="mt-4 mb-3">
          Items
        </h3>
      </b-col>
    </b-row>

    <div
      v-for="(v, index) in $v.form.items.$each.$iter"
      class="mt-3"
      :key="index"
    >
      <b-row>
        <b-col>
          <b-form-group
            label="Service"
            label-for="quote-template-item-service_id"
            class="mb-0"
          >
            <service-select
              :select-class="{ 'is-invalid': v.service_id.$dirty && v.service_id.$error }"
              class="quote-template-item-service_id mb-1"
              placeholder="Optional"
              client-unassigned
              v-model="v.service.$model"
              @input="(service) => onServiceChange(index, service)"
              :client-id="form.client_id"
            />
            <b-form-invalid-feedback
              v-if="v.service_id.$dirty"
              class="quote-template-item-service_id-feedback"
            >
              <span v-if="!v.service_id.serverFailed">{{ serverErrors.service_id }}</span>
            </b-form-invalid-feedback>
            <b-form-textarea
              class="quote-template-item-description"
              placeholder="Enter a description"
              v-model="v.description.$model"
              :state="v.description.$dirty ? !v.description.$error : null"
              rows="6"
            />
            <b-form-invalid-feedback
              v-if="v.description.$dirty"
              class="quote-template-item-description-feedback"
            >
              <span v-if="!v.description.required">Please enter a description.</span>
              <span v-if="!v.description.serverFailed">{{ serverErrors.description }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-row>
            <b-col>
              <b-form-group
                label="Category"
                label-for="quote-template-category"
              >
                <category-select
                  name="quote-template-category"
                  v-model="v.category.$model"
                  @input="(category) => onItemCategoryChange(index, v, category)"
                  :select-class="{ 'is-invalid': v.category.$dirty && v.category.$error }"
                />
                <b-form-invalid-feedback
                  v-if="v.category.$dirty"
                  class="quote-template-category-feedback"
                >
                  <span v-if="!v.category.required">Please enter a category.</span>
                  <span v-if="!v.category.maxLength">
                    The category must be less than {{ v.category.$params.maxLength.max + 1 }} characters.
                  </span>
                  <span v-if="!v.category.serverFailed">{{ serverErrors.category }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col class="text-right">
              <b-button
                class="remove-item-button font-size-lg"
                v-b-tooltip.hover
                title="Remove the Item"
                variant="link"
                @click="() => removeItem(index)"
              >
                <i class="fa fa-close" />
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="isMaterialsCategory(v.category.$model)">
            <b-col>
              <b-form-group
                :label="fieldLabels.supply_id"
                label-for="quote-template-item-supply_id"
              >
                <supply-select
                  name="quote-template-item-supply_id"
                  :value="form.items[index].supply"
                  @input="(supply) => onItemSupplyChange(index, v, supply)"
                  :select-class="{ 'is-invalid': v.supply_id.$dirty && v.supply_id.$error }"
                />
                <b-form-invalid-feedback
                  v-if="v.supply_id.$dirty"
                  class="quote-template-item-supply_id-feedback"
                >
                  <span v-if="!v.supply_id.serverFailed">{{ serverErrors.supply }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="4">
              <b-form-group
                label="Quantity"
                label-for="quote-template-quantity"
                class="mb-0"
              >
                <b-form-input
                  name="quote-template-quantity"
                  v-model="v.quantity.$model"
                  :state="v.quantity.$dirty ? !v.quantity.$error : null"
                  type="text"
                />
                <b-form-invalid-feedback
                  v-if="v.quantity.$dirty"
                  class="quote-template-quantity-feedback"
                >
                  <span v-if="!v.quantity.required">Please enter a quantity.</span>
                  <span v-if="!v.quantity.decimal">Please enter a number.</span>
                  <span v-if="!v.quantity.serverFailed">{{ serverErrors.quantity }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                label="Cost"
                label-for="quote-template-cost"
                class="mb-0"
              >
                <b-input-group
                  prepend="$"
                  :class="{ 'is-invalid': v.cost.$dirty && v.cost.$error, 'has-warning': !v.cost.$error && negativeProfitability(v) }"
                >
                  <b-form-input
                    name="quote-template-cost"
                    v-model="v.cost.$model"
                    @input="(newCost) => onItemCostChange(v, newCost)"
                    :state="v.cost.$dirty ? !v.cost.$error : null"
                    type="text"
                  />
                </b-input-group>
                <small
                  v-if="!v.cost.$error"
                  class="form-text text-secondary"
                >
                  <money-format
                    class="d-inline"
                    :value="toCents(v.cost.$model) * v.quantity.$model"
                    subunits-value
                  />
                  total
                </small>
                <b-form-invalid-feedback
                  v-if="v.cost.$dirty"
                  class="quote-template-cost-feedback"
                >
                  <span v-if="!v.cost.required">Please enter a cost.</span>
                  <span v-if="!v.cost.decimal">Please enter a number.</span>
                  <span v-if="!v.cost.serverFailed">
                    {{ serverErrors.cost_cents }}
                  </span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col lg="4">
              <b-form-group
                label="List Cost"
                label-for="quote-template-list_cost"
                class="mb-0"
              >
                <template #label>
                  List Cost
                  <i
                    class="far fa-question-circle"
                    v-b-tooltip.html.hover
                    title="The maximum the client agreed for us to pay for the item. <br /> Markup will be applied against the list cost."
                  />
                </template>
                <b-input-group
                  prepend="$"
                  :class="{ 'is-invalid': v.list_cost.$dirty && v.list_cost.$error }"
                >
                  <b-form-input
                    name="quote-template-list_cost"
                    v-model="v.list_cost.$model"
                    @input="(newCost) => onItemListCostChange(v, newCost)"
                    :state="v.list_cost.$dirty ? !v.list_cost.$error : null"
                    type="text"
                    placeholder="Optional"
                  />
                </b-input-group>

                <b-form-invalid-feedback
                  v-if="v.list_cost.$dirty"
                  :state="v.list_cost.$dirty ? !v.list_cost.$error : null"
                  class="quote-template-list_cost-feedback"
                >
                  <span v-if="!v.list_cost.decimal">Please enter a number.</span>
                  <span v-if="!v.list_cost.serverFailed">{{ serverErrors.list_cost_cents }}</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="negativeProfitability(v)">
            <b-col>
              <small class="warning-feedback">
                Warning: the cost of this item is more than the price.
              </small>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              lg="5"
            >
              <b-form-group
                label="Markup"
                label-for="quote-template-markup"
              >
                <b-input-group
                  append="%"
                  :class="{ 'is-invalid': v.markup.$dirty && v.markup.$error }"
                >
                  <b-form-input
                    name="quote-template-markup"
                    placeholder="Optional"
                    @input="(newMarkup) => onItemMarkupChange(v, newMarkup)"
                    :state="v.markup.$dirty ? !v.markup.$error : null"
                    type="text"
                  />
                </b-input-group>
                <b-form-invalid-feedback
                  v-if="v.markup.$dirty"
                  :state="v.markup.$dirty ? !v.markup.$error : null"
                  class="quote-template-markup-feedback"
                >
                  <span v-if="!v.markup.decimal">Please enter a number.</span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              lg="7"
            >
              <b-form-group
                label="Price"
                label-for="quote-template-price"
                class="mb-0"
              >
                <b-input-group
                  prepend="$"
                  :class="{ 'is-invalid': v.price.$dirty && v.price.$error }"
                >
                  <b-form-input
                    name="quote-template-price"
                    v-model="v.price.$model"
                    :state="v.price.$dirty ? !v.price.$error : null"
                    type="text"
                  />
                </b-input-group>
                <small
                  v-if="!v.price.$error"
                  class="form-text text-secondary"
                >
                  <money-format
                    class="d-inline"
                    :value="toCents(v.price.$model) * v.quantity.$model"
                    subunits-value
                  />
                  total
                </small>
                <b-form-invalid-feedback
                  v-if="v.price.$dirty"
                  class="quote-template-price-feedback"
                >
                  <span v-if="!v.price.required">Please enter a price.</span>
                  <span v-if="!v.price.decimal">Please enter a number.</span>
                  <span v-if="!v.price.serverFailed">
                    {{ serverErrors.price_cents }}
                  </span>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <b-row class="mb-3">
      <b-col>
        <b-button
          id="add-quote-template-item-button"
          class="mt-1"
          variant="outline-secondary"
          @click="addItem"
        >
          <i class="fa fa-plus mr-1" />Add Item
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col
        offset-lg="8"
        lg="2"
        order="2"
        order-lg="1"
      >
        <b-button
          variant="link-dark"
          block
          @click="$emit('on-cancel')"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        lg="2"
        order="1"
        order-lg="2"
      >
        <b-button
          block
          type="submit"
          variant="primary"
          :disabled="processing"
        >
          Save
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { required, maxLength, decimal, minLength } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import ServiceSelect from '@/components/admin/services/Select.vue';
import ClientSelect from '@/components/admin/clients/Select.vue';
import CategorySelect from '@/components/admin/workOrderCharges/CategoryButtonSelect.vue';
import SupplySelect from '@/components/admin/supplies/Select.vue';
import { toCents, roundedMoney } from '@/utils/money';

const amountFromMoney = (money, centsFallback = null) => {
  if (money && money.amount != null) {
    return money.amount / 100;
  }
  if (typeof money === 'number') {
    return money / 100;
  }
  if (centsFallback != null) {
    return centsFallback / 100;
  }
  return 0;
};

export default {
  name: 'QuoteTemplatesForm',
  components: {
    ServiceSelect,
    CategorySelect,
    ClientSelect,
    SupplySelect,
  },
  mixins: [formMixin],
  props: {
    name: String,
    description: String,
    client_id: [String, Number],
    client: Object,
    service_id: [String, Number],
    service: Object,
    newForm: {
      type: Boolean,
      default: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const sourceItems = Array.isArray(this.items) ? this.items : [];
    const mappedItems = sourceItems.length ? sourceItems.map((item) => this.toFormItem(item)) : [];
    return {
      form: {
        name: this.name || '',
        description: this.description || '',
        client_id: this.client_id || this.client?.id || null,
        client: this.client || null,
        service_id: this.service_id || this.service?.id || null,
        service: this.service || null,
        items: mappedItems.length === 0 && this.newForm ? [this.emptyItem()] : mappedItems,
      },
      fieldLabels: {
        name: 'Name',
        description: 'Description',
        client_id: 'Client',
        service_id: 'Service',
        category: 'Category',
        quantity: 'Quantity',
        cost: 'Cost',
        price: 'Price',
        list_cost: 'List Cost',
        cost_cents: 'Cost',
        price_cents: 'Price',
        list_cost_cents: 'List Cost',
        supply: 'Supply',
        supply_id: 'Supply',
      },
    };
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('name');
        },
      },
      description: {
        serverFailed() {
          return !this.hasServerErrors('description');
        },
      },
      items: {
        required,
        minLength: minLength(1),
        $each: {
          description: {
            required,
            serverFailed() {
              return !this.hasServerErrors('description');
            },
          },
          service: {},
          service_id: {
            serverFailed() {
              return !this.hasServerErrors('service_id');
            },
          },
          supply_id: {
            serverFailed() {
              return !this.hasServerErrors('supply');
            },
          },
          category: {
            required,
            maxLength: maxLength(255),
            serverFailed() {
              return !this.hasServerErrors('category');
            },
          },
          quantity: {
            required,
            decimal,
            serverFailed() {
              return !this.hasServerErrors('quantity');
            },
          },
          markup: {
            decimal,
          },
          cost: {
            required,
            decimal,
            serverFailed() {
              return !this.hasServerErrors('cost_cents');
            },
          },
          list_cost: {
            decimal,
            serverFailed() {
              return !this.hasServerErrors('list_cost_cents');
            },
          },
          price: {
            required,
            decimal,
            serverFailed() {
              return !this.hasServerErrors('price_cents');
            },
          },
        },
      },
      client_id: {
        serverFailed() {
          return !this.hasServerErrors('client_id');
        },
      },
      service_id: {
        serverFailed() {
          return !this.hasServerErrors('service_id');
        },
      },
    },
  },
  methods: {
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }
      this.processing = true;
      this.$emit('on-submit', this.serializeForm(), this.handleServerResponse);
    },
    serializeForm() {
      return {
        name: this.form.name,
        description: this.form.description,
        client_id: this.form.client_id,
        service_id: this.form.service_id,
        items_attributes: this.form.items.map((item) => ({
          id: item.id,
          description: item.description,
          service_id: item.service_id,
          supply_id: item.supply_id,
          quantity: item.quantity,
          category: item.category,
          cost_cents: this.toCents(item.cost),
          list_cost_cents: item.list_cost === null ? null : this.toCents(item.list_cost),
          price_cents: this.toCents(item.price),
        })),
      };
    },
    emptyItem() {
      return {
        id: null,
        description: null,
        service_id: null,
        service: null,
        supply_id: null,
        supply: null,
        quantity: 1,
        cost: 0,
        list_cost: null,
        markup: null,
        price: 0,
        category: 'labor',
      };
    },
    toFormItem(item) {
      return {
        id: item.id,
        description: item.description || null,
        service_id: item.service_id || item.service?.id || null,
        service: item.service || null,
        supply_id: item.supply_id || item.supply?.id || null,
        supply: item.supply || null,
        quantity: item.quantity || 1,
        cost: amountFromMoney(item.cost, item.cost_cents),
        list_cost: item.list_cost ? amountFromMoney(item.list_cost, item.list_cost_cents) : null,
        markup: null,
        price: amountFromMoney(item.price, item.price_cents),
        category: item.category || 'labor',
      };
    },
    addItem() {
      this.form.items.push(this.emptyItem());
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    isMaterialsCategory(category) {
      return (category || '').includes('material');
    },
    onItemCategoryChange(index, v, category) {
      if (!this.isMaterialsCategory(category)) {
        v.supply_id.$model = null;
        this.form.items[index].supply = null;
      }
    },
    onItemSupplyChange(index, v, supply) {
      v.supply_id.$model = supply ? supply.id : null;
      this.form.items[index].supply = supply;

      if (!supply) {
        return;
      }

      const cost = supply.cost?.amount ?? 0;
      const price = supply.price?.amount ?? 0;
      const listCost = supply.list_cost?.amount ?? null;

      v.cost.$model = cost / 100;
      v.price.$model = price / 100;
      v.list_cost.$model = listCost === null ? null : listCost / 100;

      if (!v.description.$model) {
        v.description.$model = supply.description;
      }
    },
    onServiceChange(index, service) {
      this.form.items[index].service_id = service ? service.id : null;
      this.form.items[index].service = service;
      if (service?.charge_category != null) {
        this.form.items[index].category = service.charge_category;
        if (!this.isMaterialsCategory(service.charge_category)) {
          this.form.items[index].supply_id = null;
          this.form.items[index].supply = null;
        }
      }
      if (service && !this.form.items[index].description) {
        this.form.items[index].description = service.description;
      }
    },
    onClientChange(client) {
      this.form.client_id = client ? client.id : null;
      this.form.client = client;
      if (!client) {
        this.form.service_id = null;
        this.form.service = null;
      }
    },
    onTemplateServiceChange(service) {
      this.form.service_id = service ? service.id : null;
      this.form.service = service;
      if (service && !this.form.description) {
        this.form.description = service.description;
      }
    },
    negativeProfitability(v) {
      return this.toCents(v.cost.$model) > this.toCents(v.price.$model);
    },
    toCents(amount) {
      return toCents(amount);
    },
    onItemMarkupChange(v, newMarkup) {
      const markup = isNaN(newMarkup) ? 0 : parseFloat(newMarkup / 100);
      v.markup.$model = markup * 100;
      this.updateCostChange(v);
    },
    onItemCostChange(v, newCost) {
      v.cost.$model = isNaN(newCost) ? 0 : Number.parseFloat(newCost || 0);
      this.updateCostChange(v);
    },
    onItemListCostChange(v, newListCost) {
      v.list_cost.$model = isNaN(newListCost) ? null : Number.parseFloat(newListCost);
      this.updateCostChange(v);
    },
    updateCostChange(v) {
      const costInput = (v.list_cost.$model || 0) == 0 ? v.cost.$model : v.list_cost.$model;
      if (!isNaN(costInput) && (v.markup.$model || 0) != 0) {
        const newPriceCents = costInput + (costInput * (v.markup.$model / 100));
        v.price.$model = roundedMoney(newPriceCents);
      }
    },
  },
};
</script>
